import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowUpRight,
  ArrowLeft,
  Brain,
  Dumbbell,
  Users,
  Cog,
  Check,
  X,
  MessageSquare,
  ClipboardCheck,
  BarChart3,
  BookOpen,
  Calendar,
  MessagesSquare,
  Download,
  CheckCircle2,
  FileText,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { PeakMark } from "@/components/PeakMark";
import { useSeo } from "@/hooks/useSeo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const spartanImg =
  "https://customer-assets.emergentagent.com/job_ssg-coaching/artifacts/onaa07ij_IMG_3810.JPG";
const gymImg =
  "https://customer-assets.emergentagent.com/job_ssg-coaching/artifacts/7itfajar_IMG_2292.jpg";

const pillars = [
  {
    n: "01",
    icon: Brain,
    title: "Mindset",
    lede: "High performance starts internally.",
    body: "We'll identify limiting beliefs, eliminate excuses, develop emotional resilience, and build the confidence that comes from keeping promises to yourself.",
    focus: [
      "Mental toughness",
      "Confidence",
      "Identity",
      "Stress management",
      "Decision making",
      "Self-awareness",
    ],
  },
  {
    n: "02",
    icon: Dumbbell,
    title: "Physical Performance",
    lede: "Your body drives your energy, confidence, and capacity.",
    body: "Whether you're preparing for HYROX, losing weight, building strength, or simply becoming healthier, every program is personalized around your goals.",
    focus: [
      "Training",
      "Recovery",
      "Sleep",
      "Nutrition guidance",
      "Performance tracking",
      "Sustainable habits",
    ],
  },
  {
    n: "03",
    icon: Users,
    title: "Leadership & Lifestyle",
    lede: "How you do one thing is how you do everything.",
    body: "We'll improve how you lead yourself first — then how you lead others.",
    focus: [
      "Time management",
      "Boundaries",
      "Communication",
      "Relationships",
      "Professional growth",
      "Accountability",
    ],
  },
  {
    n: "04",
    icon: Cog,
    title: "Systems",
    lede: "Motivation fades. Systems remain.",
    body: "We'll build repeatable routines that remove friction and make consistency the default.",
    focus: [
      "Morning routines",
      "Weekly planning",
      "Habit systems",
      "Goal tracking",
      "Performance reviews",
      "Environmental design",
    ],
  },
];

const phases = [
  {
    n: "01",
    name: "Stabilize",
    bullets: [
      "Create consistency",
      "Improve sleep",
      "Establish routines",
      "Reduce overwhelm",
      "Build awareness",
    ],
  },
  {
    n: "02",
    name: "Strengthen",
    bullets: [
      "Increase discipline",
      "Improve fitness",
      "Develop confidence",
      "Build momentum",
      "Refine habits",
    ],
  },
  {
    n: "03",
    name: "Perform",
    bullets: [
      "Execute at a higher level",
      "Lead better",
      "Train harder",
      "Recover smarter",
      "Take on bigger challenges",
    ],
  },
  {
    n: "04",
    name: "Sustain",
    bullets: [
      "Create long-term systems",
      "Maintain progress",
      "Prevent burnout",
      "Become self-sufficient",
    ],
  },
];

const included = [
  {
    icon: Calendar,
    title: "Weekly Strategy Calls",
    body: "45–60 minute coaching sessions. Goal review, obstacle solving, and performance planning.",
  },
  {
    icon: Dumbbell,
    title: "Customized Training Program",
    body: "Strength, conditioning, HYROX prep (if applicable), recovery, and progressions.",
  },
  {
    icon: MessagesSquare,
    title: "Unlimited Messaging Support",
    body: "Questions, feedback, wins, struggles, adjustments — real accountability between sessions.",
  },
  {
    icon: ClipboardCheck,
    title: "Weekly Check-Ins",
    body: "Wins, challenges, recovery, sleep, nutrition, mindset — and the adjustments that follow.",
  },
  {
    icon: BarChart3,
    title: "Performance Dashboard",
    body: "Track goals, habits, training, recovery, consistency, and progress.",
  },
  {
    icon: BookOpen,
    title: "Resources",
    body: "Worksheets, frameworks, reading, videos, performance tools — exclusive client materials.",
  },
];

const forList = [
  "Leaders",
  "Entrepreneurs",
  "Professionals",
  "Parents wanting more energy",
  "Athletes",
  "People tired of starting over",
  "Those willing to be coached",
  "People who value accountability",
];

const notForList = [
  "People looking for motivation instead of action",
  "People who want quick fixes",
  "People unwilling to hear honest feedback",
  "People expecting perfection",
  "Anyone who isn't ready to take ownership",
];

const testimonials = [
  {
    text: "Working with Brady completely changed the way I approach consistency…",
  },
  {
    text: "I came for fitness but ended up improving every area of my life…",
  },
  {
    text: "The accountability was exactly what I needed…",
  },
];

const faqs = [
  {
    q: "Do I need to be fit?",
    a: "No. You need to be coachable.",
  },
  {
    q: "Is this just fitness coaching?",
    a: "No. Fitness is one tool we use to improve performance in every area of life.",
  },
  {
    q: "How often do we meet?",
    a: "Weekly coaching calls plus unlimited support between sessions.",
  },
  {
    q: "Can this be done remotely?",
    a: "Yes. Everything is designed to work virtually.",
  },
  {
    q: "Do I need gym experience?",
    a: "Not at all. Programs are built around your experience and available equipment.",
  },
  {
    q: "How long do clients usually work with you?",
    a: "Most clients start with an 8- or 12-week engagement, but many continue because sustainable growth isn't built overnight.",
  },
];

const emptyForm = {
  name: "",
  email: "",
  occupation: "",
  current_challenge: "",
  life_goal: "",
  worked_with_coach: "",
  why_now: "",
  commitment_level: "",
  anything_else: "",
};

const hyroxInsides = [
  "5-week structured training plan",
  "Foundation, Specificity & Taper phases",
  "Running, strength & compromised-running work",
  "Station strategy & transitions",
  "Race-week fueling and rest",
  "Race-day pacing & common mistakes",
];

function HyroxLeadMagnet() {
  const [form, setForm] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/leads`, {
        name: form.name,
        email: form.email,
        source: "hyrox_race_prep_guide",
        resource: "hyrox_race_prep",
      });
      setResult(res.data);
      if (res.data?.download_url) {
        window.open(res.data.download_url, "_blank", "noopener,noreferrer");
      }
      toast.success("Your guide is opening in a new tab.");
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="hyrox-guide"
      data-testid="perf-hyrox-leadmagnet"
      className="py-24 md:py-32 bg-[#0A0A0A] text-white ssg-grain relative overflow-hidden"
    >
      <div
        className="pointer-events-none absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full opacity-20 blur-3xl"
        style={{ background: "radial-gradient(circle, #9B1C1C 0%, transparent 65%)" }}
      />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
        <div className="lg:col-span-7">
          <div className="flex items-center gap-4 mb-6">
            <PeakMark size={22} className="text-white/80" />
            <span className="text-[11px] tracking-[0.28em] uppercase font-bold text-[#E25555]">
              Free Guide · 7 pages
            </span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black tracking-tight leading-[1.02]">
            The HYROX<br />
            <span className="italic font-light">Race Prep Guide</span>.
          </h2>
          <p className="mt-6 text-lg text-neutral-300 leading-relaxed font-medium max-w-xl">
            Five weeks to a stronger start line — a structured plan to build the
            engine, strength, and race-day strategy to show up prepared for
            every station.
          </p>
          <ul className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-3">
            {hyroxInsides.map((item, i) => (
              <li
                key={item}
                data-testid={`hyrox-inside-${i}`}
                className="flex items-start gap-3 text-sm text-neutral-300 font-medium"
              >
                <span className="mt-2 w-3 h-px bg-[#E25555] flex-none" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-5">
          <div className="bg-[#141414] border border-white/10 p-8 md:p-10">
            {result ? (
              <div data-testid="hyrox-success">
                <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase font-bold text-[#E25555] mb-4">
                  <CheckCircle2 size={16} strokeWidth={1.8} />
                  Delivered
                </div>
                <h3 className="font-display text-2xl font-black tracking-tight">
                  Your guide is opening.
                </h3>
                <p className="mt-3 text-[14px] text-neutral-400 leading-relaxed">
                  If your browser blocked the tab, use the button below.
                </p>
                <a
                  href={result.download_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hyrox-download-again"
                  className="mt-6 inline-flex items-center gap-3 bg-white text-black hover:bg-[#E25555] hover:text-white px-6 py-3 text-[11px] tracking-[0.24em] uppercase font-bold transition-colors"
                >
                  <Download size={15} strokeWidth={1.6} />
                  Download again
                </a>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="hyrox-form">
                <div className="flex items-center gap-2 mb-3 text-[10px] tracking-[0.22em] uppercase font-bold text-[#E25555]">
                  <FileText size={13} strokeWidth={1.8} />
                  Get the PDF
                </div>
                <h3 className="font-display text-2xl font-black tracking-tight leading-tight">
                  Send it to my inbox.
                </h3>
                <p className="mt-2 text-[13px] text-neutral-400 leading-relaxed">
                  Free download. No spam — just the guide.
                </p>
                <div className="mt-6 space-y-5">
                  <input
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    required
                    placeholder="Your name"
                    data-testid="hyrox-input-name"
                    className="w-full bg-transparent border-0 border-b border-white/20 focus:border-[#E25555] focus:ring-0 outline-none px-0 py-3 text-base text-white placeholder:text-neutral-500 transition-colors"
                  />
                  <input
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    required
                    placeholder="you@email.com"
                    data-testid="hyrox-input-email"
                    className="w-full bg-transparent border-0 border-b border-white/20 focus:border-[#E25555] focus:ring-0 outline-none px-0 py-3 text-base text-white placeholder:text-neutral-500 transition-colors"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="hyrox-submit"
                  className="group mt-7 w-full inline-flex items-center justify-center gap-3 bg-white text-black hover:bg-[#E25555] hover:text-white disabled:opacity-60 disabled:cursor-not-allowed px-6 py-4 text-[11px] tracking-[0.24em] uppercase font-bold transition-colors"
                >
                  {loading ? "Sending…" : "Send Me The Guide"}
                  <ArrowUpRight
                    size={16}
                    strokeWidth={1.6}
                    className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                  />
                </button>
                <p className="mt-4 text-[10px] tracking-[0.18em] uppercase font-bold text-neutral-500 text-center">
                  Instant PDF download
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function PerformancePage() {
  useSeo({
    title: "Performance Coaching for Busy Professionals | Smith Strategic Group",
    description:
      "Build strength, consistency, discipline, and energy with personalized performance coaching for busy professionals, leaders, and ambitious high performers.",
    path: "/performance",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Performance Coaching",
      provider: { "@type": "Organization", name: "Smith Strategic Group" },
      areaServed: ["United States", "Northern Virginia", "Washington, DC"],
      description:
        "Personalized performance coaching combining fitness programming, accountability, performance habits, recovery, mindset, and execution.",
    },
  });

  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const goApply = () =>
    document.getElementById("apply")?.scrollIntoView({ behavior: "smooth" });

  const submit = async (e) => {
    e.preventDefault();
    const required = ["name", "email", "current_challenge", "life_goal", "why_now"];
    for (const k of required) {
      if (!String(form[k] || "").trim()) {
        toast.error("Please complete all required fields.");
        return;
      }
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        commitment_level: form.commitment_level ? Number(form.commitment_level) : null,
      };
      await axios.post(`${API}/applications`, payload);
      setSent(true);
      setForm(emptyForm);
      toast.success("Application received. Brady will be in touch soon.");
      window.scrollTo({ top: document.getElementById("apply").offsetTop - 80, behavior: "smooth" });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="App font-body antialiased text-neutral-950 bg-white">
      <Navbar />

      {/* ============== HERO ============== */}
      <section
        data-testid="perf-hero"
        className="relative bg-[#0A0A0A] text-white pt-32 pb-24 md:pt-40 md:pb-32 overflow-hidden ssg-grain"
      >
        <div
          className="pointer-events-none absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full opacity-25 blur-3xl"
          style={{ background: "radial-gradient(circle, #9B1C1C 0%, transparent 65%)" }}
        />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Link
              to="/"
              data-testid="perf-back-home"
              className="inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase font-bold text-neutral-500 hover:text-white transition-colors mb-8"
            >
              <ArrowLeft size={14} strokeWidth={1.6} />
              Back to Smith Strategic Group
            </Link>
            <div className="flex items-center gap-4 mb-8">
              <PeakMark size={30} className="text-white/80" />
              <span className="text-[11px] tracking-[0.28em] uppercase font-bold text-[#E25555]">
                SSG Performance Coaching
              </span>
            </div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              data-testid="perf-hero-headline"
              className="font-display text-5xl md:text-6xl lg:text-7xl xl:text-[88px] font-black tracking-tight leading-[0.98]"
            >
              Stop negotiating<br />
              with your{" "}
              <span className="italic font-light">potential</span>
              <span className="text-[#E25555]">.</span>
            </motion.h1>
            <p className="mt-8 max-w-xl text-lg md:text-xl text-neutral-300 leading-relaxed font-medium">
              Performance isn&apos;t built through motivation. It&apos;s built
              through standards, systems, and relentless execution.
            </p>
            <p className="mt-5 max-w-xl text-[15px] md:text-base text-neutral-400 leading-relaxed">
              SSG Performance Coaching helps ambitious people become the kind
              of person who follows through — at work, in the gym, and in
              life.
            </p>
            <div className="mt-10">
              <button
                onClick={goApply}
                data-testid="perf-hero-cta"
                className="group inline-flex items-center gap-3 bg-white text-black hover:bg-[#E25555] hover:text-white px-8 py-4 text-[11px] tracking-[0.24em] uppercase font-bold transition-all duration-300"
              >
                Apply for Performance Coaching
                <ArrowUpRight
                  size={16}
                  strokeWidth={1.6}
                  className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                />
              </button>
            </div>
          </div>
          <div className="lg:col-span-5 relative hidden lg:block">
            <div className="relative aspect-[4/5] w-full overflow-hidden bg-neutral-900">
              <img
                src={spartanImg}
                alt="Brady Smith competing — the standard, in the field"
                className="w-full h-full object-cover object-center contrast-[1.05]"
              />
              <div className="absolute -bottom-px -right-px h-24 w-24 bg-[#9B1C1C]" />
              <div className="absolute top-6 left-6 right-6 flex items-center justify-between text-white">
                <span className="text-[10px] tracking-[0.3em] uppercase font-bold">Coach / Athlete</span>
                <span className="text-[10px] tracking-[0.3em] uppercase font-bold">No Compromise</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== PHILOSOPHY ============== */}
      <section data-testid="perf-philosophy" className="py-24 md:py-32 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-4 mb-8">
            <PeakMark size={20} className="text-neutral-900" />
            <span className="text-[11px] tracking-[0.28em] uppercase font-bold text-[#9B1C1C]">
              The Philosophy
            </span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-neutral-950 leading-[1.02]">
            The <span className="italic font-light">honest</span> scoreboard.
          </h2>

          <div className="mt-14 grid lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-5">
              <p className="text-lg leading-relaxed text-neutral-800 font-medium">
                Most people already know what they need to do.
              </p>
              <ul className="mt-5 space-y-2 text-lg text-neutral-800">
                {["Train.", "Sleep.", "Communicate.", "Lead.", "Be consistent."].map((w) => (
                  <li key={w} className="font-display italic font-light">
                    {w}
                  </li>
                ))}
              </ul>
              <p className="mt-8 text-lg leading-relaxed text-neutral-700 font-medium">
                The problem isn&apos;t knowledge.
                <br />
                The problem is <span className="text-[#9B1C1C] font-semibold">accountability</span>.
              </p>
            </div>
            <div className="lg:col-span-7 space-y-6 text-[17px] leading-relaxed text-neutral-700 font-medium">
              <p>
                We don&apos;t coach motivation.
                <br />
                <span className="text-neutral-950 font-bold">We coach standards.</span>
              </p>
              <p>
                The gym becomes the laboratory where discipline is tested. Business
                becomes the proving ground where leadership is measured. Life becomes
                the scoreboard.
              </p>
              <p className="pt-6 border-t border-neutral-200">
                You don&apos;t need another motivational quote. You need someone
                willing to tell you the truth, hold you accountable, and help you
                execute.
              </p>
              <p className="font-display italic text-2xl text-neutral-950 leading-snug">
                That&apos;s what this coaching is built on.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============== FOUR PILLARS ============== */}
      <section
        data-testid="perf-pillars"
        className="py-24 md:py-32 bg-[#0A0A0A] text-white ssg-grain relative overflow-hidden"
      >
        <div
          className="pointer-events-none absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full opacity-15 blur-3xl"
          style={{ background: "radial-gradient(circle, #9B1C1C 0%, transparent 65%)" }}
        />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-4 mb-8">
            <PeakMark size={20} className="text-white/80" />
            <span className="text-[11px] tracking-[0.28em] uppercase font-bold text-[#E25555]">
              The Four Pillars
            </span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black tracking-tight leading-[1.02] max-w-3xl">
            Everything we build{" "}
            <span className="italic font-light">rests on four things</span>.
          </h2>

          <div className="mt-16 grid md:grid-cols-2 gap-px bg-white/10">
            {pillars.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div
                  key={p.n}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  data-testid={`perf-pillar-${i}`}
                  className="bg-[#0A0A0A] p-10 lg:p-14 relative group hover:bg-[#141414] transition-colors overflow-hidden"
                >
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute -top-6 -right-4 font-display text-[180px] font-black tracking-tighter text-white/[0.04] leading-none select-none"
                  >
                    {p.n}
                  </span>
                  <div className="flex items-center justify-between mb-8 relative">
                    <div className="relative flex items-center justify-center w-16 h-16">
                      <span className="absolute inset-0 rounded-full border border-[#9B1C1C]/50 group-hover:border-[#E25555] transition-colors" />
                      <span className="absolute inset-2 rounded-full bg-[#9B1C1C]/10 group-hover:bg-[#9B1C1C]/20 transition-colors" />
                      <Icon size={30} strokeWidth={1.5} className="text-white relative" />
                    </div>
                    <span className="font-display text-sm font-bold tracking-[0.2em] text-[#E25555]">
                      /{p.n}
                    </span>
                  </div>
                  <h3 className="font-display text-3xl md:text-4xl font-black tracking-tight text-white">
                    {p.title}
                  </h3>
                  <p className="mt-5 font-display italic text-xl text-neutral-200">
                    {p.lede}
                  </p>
                  <p className="mt-4 text-[15px] leading-relaxed text-neutral-400 font-medium">
                    {p.body}
                  </p>
                  <div className="mt-8 pt-8 border-t border-white/10">
                    <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-neutral-500 mb-4">
                      Focus Areas
                    </div>
                    <ul className="grid grid-cols-2 gap-x-6 gap-y-3">
                      {p.focus.map((f) => (
                        <li
                          key={f}
                          className="flex items-start gap-3 text-[13px] text-neutral-300"
                        >
                          <span className="mt-2 w-2 h-px bg-[#E25555] flex-none" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============== PROGRESSION ============== */}
      <section data-testid="perf-progression" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 mb-14 items-end">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-4 mb-6">
                <PeakMark size={20} className="text-neutral-900" />
                <span className="text-[11px] tracking-[0.28em] uppercase font-bold text-[#9B1C1C]">
                  The Performance Progression
                </span>
              </div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-neutral-950 leading-[1.02]">
                Every client moves<br />
                through <span className="italic font-light">four phases</span>.
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="font-display italic text-xl md:text-2xl text-neutral-700 leading-snug max-w-md">
                This isn&apos;t about changing your life for eight weeks. It&apos;s
                about changing who you become.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-neutral-200 border border-neutral-200">
            {phases.map((ph, i) => (
              <motion.div
                key={ph.n}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                data-testid={`perf-phase-${i}`}
                className="bg-white p-8 lg:p-10 group relative overflow-hidden hover:bg-neutral-950 transition-colors duration-500"
              >
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute -top-4 -right-2 font-display text-[140px] font-black tracking-tighter text-neutral-950/[0.04] group-hover:text-white/[0.06] leading-none select-none transition-colors"
                >
                  {ph.n}
                </span>
                <div className="relative">
                  <span className="font-display text-sm font-bold tracking-[0.2em] text-[#9B1C1C] group-hover:text-[#E25555] transition-colors">
                    /{ph.n}
                  </span>
                  <h3 className="mt-4 font-display text-3xl font-black tracking-tight text-neutral-950 group-hover:text-white transition-colors">
                    {ph.name}
                  </h3>
                  <ul className="mt-6 space-y-3">
                    {ph.bullets.map((b) => (
                      <li
                        key={b}
                        className="flex items-start gap-3 text-[14px] text-neutral-700 group-hover:text-neutral-300 transition-colors"
                      >
                        <span className="mt-2 w-3 h-px bg-[#9B1C1C] flex-none" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== WHAT'S INCLUDED ============== */}
      <section data-testid="perf-included" className="py-24 md:py-32 bg-[#F7F6F3]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-4 mb-6">
            <PeakMark size={20} className="text-neutral-900" />
            <span className="text-[11px] tracking-[0.28em] uppercase font-bold text-[#9B1C1C]">
              What&apos;s Included
            </span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-neutral-950 leading-[1.02] max-w-3xl">
            Every coaching relationship{" "}
            <span className="italic font-light">includes</span> —
          </h2>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-neutral-200 border border-neutral-200">
            {included.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div
                  key={f.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.45, delay: i * 0.04 }}
                  data-testid={`perf-included-${i}`}
                  className="bg-white p-8 lg:p-10"
                >
                  <div className="relative flex items-center justify-center w-14 h-14 mb-6">
                    <span className="absolute inset-0 rounded-full border border-[#9B1C1C]/40" />
                    <span className="absolute inset-1.5 rounded-full bg-[#9B1C1C]/8" />
                    <Icon size={22} strokeWidth={1.5} className="text-neutral-900 relative" />
                  </div>
                  <h3 className="font-display text-xl md:text-2xl font-bold tracking-tight text-neutral-950">
                    {f.title}
                  </h3>
                  <p className="mt-3 text-[14px] leading-relaxed text-neutral-600 font-medium">
                    {f.body}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============== WHO / WHO NOT ============== */}
      <section
        data-testid="perf-who"
        className="py-24 md:py-32 bg-[#0A0A0A] text-white ssg-grain relative overflow-hidden"
      >
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid md:grid-cols-2 gap-16 lg:gap-24">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <Check size={18} strokeWidth={2} className="text-[#E25555]" />
                <span className="text-[11px] tracking-[0.28em] uppercase font-bold text-[#E25555]">
                  Who this is for
                </span>
              </div>
              <h3 className="font-display text-3xl md:text-4xl font-black tracking-tight leading-[1.05]">
                Ambitious. Coachable.<br />
                <span className="italic font-light">Ready.</span>
              </h3>
              <ul className="mt-10 space-y-4">
                {forList.map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-4 text-[15px] text-neutral-200 font-medium"
                  >
                    <Check
                      size={18}
                      strokeWidth={2.2}
                      className="text-[#E25555] flex-none mt-0.5"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="flex items-center gap-4 mb-6">
                <X size={18} strokeWidth={2} className="text-neutral-500" />
                <span className="text-[11px] tracking-[0.28em] uppercase font-bold text-neutral-500">
                  Who this is not for
                </span>
              </div>
              <h3 className="font-display text-3xl md:text-4xl font-black tracking-tight leading-[1.05] text-neutral-400">
                Bystanders.<br />
                <span className="italic font-light">Tourists. Excuses.</span>
              </h3>
              <ul className="mt-10 space-y-4">
                {notForList.map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-4 text-[15px] text-neutral-500 font-medium"
                  >
                    <X
                      size={18}
                      strokeWidth={2.2}
                      className="text-neutral-600 flex-none mt-0.5"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-10 pt-8 border-t border-white/10 text-[14px] text-neutral-500 leading-relaxed max-w-md">
                This coaching is demanding because meaningful change requires
                honesty, commitment, and execution.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============== MY STORY ============== */}
      <section data-testid="perf-story" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="relative aspect-[4/5] w-full overflow-hidden bg-neutral-900">
              <img
                src={gymImg}
                alt="Brady Smith — training the standard, every day"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute -bottom-px -right-px h-20 w-20 bg-[#9B1C1C]" />
              <div className="absolute top-5 left-5 right-5 flex items-center justify-between text-white">
                <span className="text-[10px] tracking-[0.3em] uppercase font-bold">The Standard</span>
                <span className="text-[10px] tracking-[0.3em] uppercase font-bold">Every Day</span>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2">
            <div className="flex items-center gap-4 mb-6">
              <PeakMark size={20} className="text-neutral-900" />
              <span className="text-[11px] tracking-[0.28em] uppercase font-bold text-[#9B1C1C]">
                My Story
              </span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-neutral-950 leading-[1.02]">
              Why this <span className="italic font-light">works</span>.
            </h2>

            <div className="mt-10 space-y-6 text-[17px] leading-relaxed text-neutral-700 font-medium">
              <p>
                I didn&apos;t build this framework from a textbook. I built it
                because I needed it.
              </p>
              <p>
                There was a time when I struggled with depression, anxiety,
                inconsistency, and constantly feeling like I was capable of
                more than I was becoming.
              </p>
              <p>
                Then I stopped waiting for motivation.
              </p>
              <ul className="pl-6 space-y-2">
                <li className="font-display italic text-xl text-neutral-950">
                  I completed 75 Hard.
                </li>
                <li className="font-display italic text-xl text-neutral-950">
                  I raced Spartan courses.
                </li>
                <li className="font-display italic text-xl text-neutral-950">
                  I committed to becoming someone who follows through.
                </li>
              </ul>
              <p>
                That mindset transformed every area of my life — not just my
                fitness, but my leadership, career, relationships, and
                confidence.
              </p>
              <p>
                Today I combine over a decade of leadership, coaching,
                fundraising, organizational strategy, and high-performance
                training to help others build the same systems that changed my
                life.
              </p>
              <p className="pt-6 border-t border-neutral-200 font-display italic text-2xl text-neutral-950 leading-snug">
                Real confidence isn&apos;t something you&apos;re given.
                <br />
                <span className="text-[#9B1C1C] not-italic font-bold">It&apos;s earned.</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============== HYROX LEAD MAGNET ============== */}
      <HyroxLeadMagnet />

      {/* ============== RESULTS / TESTIMONIALS ============== */}
      <section data-testid="perf-results" className="py-24 md:py-32 bg-[#F7F6F3]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-4 mb-6">
            <PeakMark size={20} className="text-neutral-900" />
            <span className="text-[11px] tracking-[0.28em] uppercase font-bold text-[#9B1C1C]">
              Results
            </span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-neutral-950 leading-[1.02] max-w-3xl">
            What clients{" "}
            <span className="italic font-light">are saying</span>.
          </h2>

          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div
                key={i}
                data-testid={`perf-testimonial-${i}`}
                className="bg-white p-8 border border-neutral-200 relative"
              >
                <div className="flex gap-1 mb-6 text-[#9B1C1C]">
                  {"★★★★★".split("").map((s, si) => (
                    <span key={si} className="text-lg">
                      {s}
                    </span>
                  ))}
                </div>
                <p className="font-display italic text-lg text-neutral-800 leading-snug">
                  &ldquo;{t.text}&rdquo;
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== THE SSG COMMITMENT ============== */}
      <section
        data-testid="perf-commitment"
        className="relative py-24 md:py-32 bg-[#0A0A0A] text-white ssg-grain overflow-hidden"
      >
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.05]"
          style={{
            background:
              "radial-gradient(circle at 50% 50%, #E25555 0%, transparent 60%)",
          }}
        />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <div className="inline-flex items-center gap-3 mb-8">
            <PeakMark size={22} className="text-[#E25555]" />
            <span className="text-[11px] tracking-[0.28em] uppercase font-bold text-[#E25555]">
              The SSG Commitment
            </span>
            <PeakMark size={22} className="text-[#E25555]" />
          </div>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-black tracking-tight leading-[1.15]">
            <span className="italic font-light">I will</span> show up prepared.<br />
            <span className="italic font-light">I will</span> tell you the truth.<br />
            <span className="italic font-light">I will</span> challenge you.<br />
            <span className="italic font-light">I will</span> celebrate your wins.<br />
            <span className="italic font-light">I will</span> expect your best effort —{" "}
            <span className="text-[#E25555]">not perfection</span>.
          </h2>

          <div className="mt-16 pt-16 border-t border-white/10">
            <p className="text-lg md:text-xl text-neutral-300 leading-relaxed font-medium">
              In return, I ask for one thing:
            </p>
            <p className="mt-3 font-display text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-white">
              Your commitment<span className="text-[#E25555]">.</span>
            </p>
            <p className="mt-8 max-w-2xl mx-auto font-display italic text-xl md:text-2xl text-neutral-300 leading-snug">
              Everything changes when you decide that your{" "}
              <span className="text-white not-italic font-semibold">standards</span>{" "}
              matter more than your{" "}
              <span className="text-white not-italic font-semibold">excuses</span>.
            </p>
          </div>

          <div className="mt-14">
            <button
              onClick={goApply}
              data-testid="perf-commitment-cta"
              className="group inline-flex items-center gap-3 bg-white text-black hover:bg-[#E25555] hover:text-white px-8 py-4 text-[11px] tracking-[0.24em] uppercase font-bold transition-all duration-300"
            >
              I&apos;m ready — start my application
              <ArrowUpRight size={16} strokeWidth={1.6} />
            </button>
          </div>
        </div>
      </section>

      {/* ============== APPLICATION ============== */}
      <section
        id="apply"
        data-testid="perf-apply"
        className="py-24 md:py-32 bg-white"
      >
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-4 mb-6">
            <PeakMark size={20} className="text-neutral-900" />
            <span className="text-[11px] tracking-[0.28em] uppercase font-bold text-[#9B1C1C]">
              Apply
            </span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-neutral-950 leading-[1.02]">
            Ready to raise<br />
            your <span className="italic font-light">standard</span>?
          </h2>
          <p className="mt-6 text-[17px] text-neutral-600 leading-relaxed font-medium max-w-2xl">
            Applications help ensure every client is a good fit. Take a few
            minutes to answer these honestly — the more real you are here, the
            more useful our first conversation will be.
          </p>

          {sent ? (
            <div
              data-testid="perf-apply-success"
              className="mt-14 border border-neutral-200 p-10 md:p-14"
            >
              <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase font-bold text-[#9B1C1C] mb-5">
                <MessageSquare size={16} strokeWidth={1.8} />
                Application received
              </div>
              <h3 className="font-display text-3xl md:text-4xl font-black tracking-tight text-neutral-950">
                Thank you.
              </h3>
              <p className="mt-5 text-neutral-600 leading-relaxed font-medium">
                I read every application personally. If we&apos;re a fit,
                I&apos;ll be in touch shortly to schedule a conversation.
              </p>
              <button
                type="button"
                onClick={() => setSent(false)}
                data-testid="perf-apply-reset"
                className="mt-8 inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-bold text-neutral-900"
              >
                Submit another application
                <ArrowUpRight size={14} strokeWidth={1.6} />
              </button>
            </div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="perf-apply-form"
              className="mt-14 space-y-10"
            >
              <div className="grid sm:grid-cols-2 gap-10">
                <Field
                  label="Name"
                  required
                  testid="perf-input-name"
                  value={form.name}
                  onChange={update("name")}
                />
                <Field
                  label="Email"
                  type="email"
                  required
                  testid="perf-input-email"
                  value={form.email}
                  onChange={update("email")}
                />
              </div>
              <Field
                label="Occupation"
                testid="perf-input-occupation"
                value={form.occupation}
                onChange={update("occupation")}
                placeholder="What do you do?"
              />
              <FieldArea
                label="Current biggest challenge"
                required
                testid="perf-input-challenge"
                value={form.current_challenge}
                onChange={update("current_challenge")}
                placeholder="Where are you stuck right now?"
              />
              <FieldArea
                label="What goal would change your life over the next 6–12 months?"
                required
                testid="perf-input-goal"
                value={form.life_goal}
                onChange={update("life_goal")}
              />
              <FieldArea
                label="Have you worked with a coach before?"
                testid="perf-input-coach"
                value={form.worked_with_coach}
                onChange={update("worked_with_coach")}
                placeholder="Yes / No — and briefly what worked or didn't."
              />
              <FieldArea
                label="What makes now the right time?"
                required
                testid="perf-input-why-now"
                value={form.why_now}
                onChange={update("why_now")}
              />
              <div>
                <span className="block text-[11px] tracking-[0.22em] uppercase font-bold text-neutral-500 mb-4">
                  How committed are you on a scale from 1–10?
                </span>
                <div className="flex flex-wrap gap-2">
                  {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => {
                    const selected = String(form.commitment_level) === String(n);
                    return (
                      <button
                        key={n}
                        type="button"
                        onClick={() =>
                          setForm((f) => ({ ...f, commitment_level: String(n) }))
                        }
                        data-testid={`perf-input-commitment-${n}`}
                        className={`w-12 h-12 text-sm font-bold tracking-tight border transition-all ${
                          selected
                            ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                            : "bg-white text-neutral-900 border-neutral-300 hover:border-[#9B1C1C]"
                        }`}
                      >
                        {n}
                      </button>
                    );
                  })}
                </div>
              </div>
              <FieldArea
                label="Anything else you'd like me to know?"
                testid="perf-input-anything"
                value={form.anything_else}
                onChange={update("anything_else")}
              />

              <div className="flex items-center justify-between flex-wrap gap-6 pt-4">
                <p className="text-[11px] tracking-[0.22em] uppercase font-bold text-neutral-400 max-w-xs">
                  Read personally. Nothing shared. No lists.
                </p>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="perf-apply-submit"
                  className="group inline-flex items-center gap-3 bg-[#0A0A0A] hover:bg-[#9B1C1C] disabled:opacity-60 disabled:cursor-not-allowed text-white px-8 py-4 text-[11px] tracking-[0.24em] uppercase font-bold transition-colors"
                >
                  {loading ? "Sending…" : "Apply Now"}
                  <ArrowUpRight
                    size={16}
                    strokeWidth={1.6}
                    className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                  />
                </button>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* ============== FAQ ============== */}
      <section data-testid="perf-faq" className="py-24 md:py-32 bg-[#F7F6F3]">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-4 mb-6">
            <PeakMark size={20} className="text-neutral-900" />
            <span className="text-[11px] tracking-[0.28em] uppercase font-bold text-[#9B1C1C]">
              Common Questions
            </span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-neutral-950 leading-[1.02]">
            The <span className="italic font-light">honest</span> answers.
          </h2>

          <div className="mt-14 border-t border-neutral-300">
            {faqs.map((f, i) => (
              <div
                key={f.q}
                data-testid={`perf-faq-${i}`}
                className="border-b border-neutral-300 py-8 grid md:grid-cols-12 gap-6"
              >
                <div className="md:col-span-5">
                  <div className="flex items-start gap-4">
                    <span className="font-display text-sm font-bold tracking-[0.2em] text-[#9B1C1C] mt-1">
                      /0{i + 1}
                    </span>
                    <h3 className="font-display text-xl md:text-2xl font-bold tracking-tight text-neutral-950 leading-snug">
                      {f.q}
                    </h3>
                  </div>
                </div>
                <div className="md:col-span-7 text-[15px] text-neutral-700 leading-relaxed font-medium">
                  {f.a}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== FINAL CTA ============== */}
      <section
        data-testid="perf-final-cta"
        className="py-24 md:py-32 bg-[#0A0A0A] text-white ssg-grain"
      >
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <PeakMark size={40} className="text-[#E25555] mx-auto mb-8" />
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">
            You already know<br />
            what you&apos;re capable of.
          </h2>
          <p className="mt-8 font-display italic text-2xl md:text-3xl text-neutral-300 font-light">
            The question is whether you&apos;re ready<br className="hidden md:block" />
            to stop negotiating with it.
          </p>
          <div className="mt-14">
            <button
              onClick={goApply}
              data-testid="perf-final-cta-btn"
              className="group inline-flex items-center gap-3 bg-white text-black hover:bg-[#E25555] hover:text-white px-8 py-4 text-[11px] tracking-[0.24em] uppercase font-bold transition-all duration-300"
            >
              Apply for Performance Coaching
              <ArrowUpRight size={16} strokeWidth={1.6} />
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

const Field = ({ label, required, value, onChange, type = "text", placeholder, testid }) => (
  <label className="block">
    <span className="block text-[11px] tracking-[0.22em] uppercase font-bold text-neutral-500 mb-3">
      {label} {required && <span className="text-[#9B1C1C]">*</span>}
    </span>
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      data-testid={testid}
      className="w-full bg-transparent border-0 border-b border-neutral-300 focus:border-[#9B1C1C] focus:ring-0 outline-none px-0 py-3 text-lg text-neutral-950 placeholder:text-neutral-400 transition-colors"
    />
  </label>
);

const FieldArea = ({ label, required, value, onChange, placeholder, testid }) => (
  <label className="block">
    <span className="block text-[11px] tracking-[0.22em] uppercase font-bold text-neutral-500 mb-3">
      {label} {required && <span className="text-[#9B1C1C]">*</span>}
    </span>
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      rows={3}
      data-testid={testid}
      className="w-full bg-transparent border-0 border-b border-neutral-300 focus:border-[#9B1C1C] focus:ring-0 outline-none px-0 py-3 text-lg text-neutral-950 placeholder:text-neutral-400 resize-none transition-colors"
    />
  </label>
);
